import React, { useEffect, useMemo, useRef, useState } from 'react'
import { HiCheck } from 'react-icons/hi2'
import { FiSearch } from 'react-icons/fi'
import { BsThreeDots } from 'react-icons/bs'
import { FiLock, FiInbox } from 'react-icons/fi'

/**
 * Quick chip selector: top N options shown as pills + a "⋯ More" popover for the rest.
 * Props:
 *  label, Icon, color ('brand'|'emerald'|'rose'|'amber'), options:[{value,label,count}],
 *  value, onChange(value), allLabel (optional => adds an "All" chip), topN (default 3), disabled,
 *  disabledText (message jab disabled ho, e.g. "Select state first"),
 *  emptyText (message jab options empty ho, e.g. "No cities found")
 */
export default function QuickPick({ label, Icon, color = 'brand', options = [], value, onChange, allLabel, topN = 3, disabled = false, disabledText = "Please select state", emptyText = "No city available in this state" }) {
    const [open, setOpen] = useState(false)
    const [q, setQ] = useState('')
    const ref = useRef()

    useEffect(() => {
        const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false) }
        document.addEventListener('click', close)
        return () => document.removeEventListener('click', close)
    }, [])

    const palette = {
        brand: { on: 'bg-gradient-to-b from-brand-400 to-brand-500 text-white border-brand-500 shadow-brand', off: 'bg-white text-brand-700 border-brand-200 hover:border-brand-400 hover:bg-brand-50', ic: 'text-brand-600', ring: 'ring-brand-400/40', soft: 'text-brand-500' },
        emerald: { on: 'bg-gradient-to-b from-emerald-400 to-emerald-500 text-white border-emerald-500 shadow-soft', off: 'bg-white text-emerald-700 border-emerald-200 hover:border-emerald-400 hover:bg-emerald-50', ic: 'text-emerald-600', ring: 'ring-emerald-400/40', soft: 'text-emerald-500' },
        rose: { on: 'bg-gradient-to-b from-rose-400 to-rose-500 text-white border-rose-500 shadow-soft', off: 'bg-white text-rose-700 border-rose-200 hover:border-rose-400 hover:bg-rose-50', ic: 'text-rose-600', ring: 'ring-rose-400/40', soft: 'text-rose-500' },
        amber: { on: 'bg-gradient-to-b from-amber-400 to-amber-500 text-white border-amber-500 shadow-soft', off: 'bg-white text-amber-700 border-amber-200 hover:border-amber-400 hover:bg-amber-50', ic: 'text-amber-600', ring: 'ring-amber-400/40', soft: 'text-amber-500' },
    }[color] || {}

    const top = options.slice(0, topN)
    const selected = options.find(o => String(o.value) === String(value))
    const selectedInTop = selected && top.some(o => String(o.value) === String(selected.value))
    const restCount = Math.max(0, options.length - top.length)

    const filtered = useMemo(() => {
        const s = q.trim().toLowerCase()
        return s ? options.filter(o => o.label.toLowerCase().includes(s)) : options
    }, [q, options])

    const pick = (v) => { onChange(v); setOpen(false); setQ('') }

    const Chip = ({ active, onClick, children, count }) => (
        <button type="button" disabled={disabled} onClick={onClick}
            className={`inline-flex items-center gap-1 rounded-full border px-4 py-2.5 text-sm font-semibold transition-all duration-150 active:scale-95
                ${active ? palette.on : palette.off}
                ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}>
            {active && <HiCheck className="text-base -ml-0.5" />}
            <span className="truncate max-w-[9rem]">{children}</span>
            {typeof count === 'number' && <span className={`text-[11px] font-medium ${active ? 'text-white/80' : 'opacity-60'}`}>({count})</span>}
        </button>
    )

    // disabled -> locked placeholder (e.g. City tab jab tak state select na ho)
    const showDisabled = disabled
    // enabled but no options -> empty placeholder (e.g. is state me koi city nahi)
    const showEmpty = !disabled && options.length === 0

    return (
        <div className="relative w-full" ref={ref}>
            <label className="mb-2 flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-gray-500">
                {Icon && <span className={`text-sm ${palette.ic}`}>{Icon}</span>}{label}
            </label>

            {showDisabled ? (
                <div className="flex items-center gap-2 rounded-2xl border border-dashed border-gray-200 bg-gray-50/60 px-4 py-3.5 text-sm text-gray-400">
                    <FiLock className="shrink-0" />
                    <span>{disabledText || `Select above to choose ${label.toLowerCase()}`}</span>
                </div>
            ) : showEmpty ? (
                <div className="flex items-center gap-2 rounded-2xl border border-dashed border-gray-200 bg-gray-50/60 px-4 py-3.5 text-sm text-gray-400">
                    <FiInbox className={`shrink-0 ${palette.soft}`} />
                    <span>{emptyText || `No ${label.toLowerCase()} found`}</span>
                </div>
            ) : (
                <div className="flex flex-wrap items-center gap-2">
                    {allLabel !== undefined &&
                        <Chip active={!value || String(value) === '0'} onClick={() => pick(0)}>{allLabel}</Chip>}

                    {top.map(o => (
                        <Chip key={o.value} active={String(o.value) === String(value)} onClick={() => pick(o.value)} count={o.count}>{o.label}</Chip>
                    ))}

                    {/* selected item that is outside top N -> show it so it's visible */}
                    {selected && !selectedInTop &&
                        <Chip active onClick={() => { }}>{selected.label}</Chip>}

                    {restCount > 0 &&
                        <button type="button" disabled={disabled} onClick={() => setOpen(o => !o)}
                            className={`inline-flex items-center gap-1.5 rounded-full border border-dashed border-gray-300 bg-gray-50 px-4 py-2.5 text-sm font-semibold text-gray-600 transition hover:border-gray-400 hover:bg-gray-100 active:scale-95
                                ${open ? `ring-2 ${palette.ring}` : ''} ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}>
                            <BsThreeDots className="text-base" /> {restCount} more
                        </button>}
                </div>
            )}

            {/* dropdown — poore container ke relative, mobile pe full width, desktop pe w-72 */}
            {open && restCount > 0 && !showDisabled && !showEmpty &&
                <div className="absolute left-0 top-full z-30 mt-2 w-full max-w-full overflow-hidden rounded-2xl bg-white shadow-pop ring-1 ring-gray-100 animate-scale-in origin-top sm:w-72 sm:origin-top-left">
                    <div className="border-b border-gray-100 p-2.5">
                        <div className="relative">
                            <FiSearch className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
                            <input autoFocus value={q} onChange={e => setQ(e.target.value)} placeholder={`Search ${label.toLowerCase()}...`}
                                className="w-full rounded-xl bg-gray-50 py-3 pl-10 pr-3 text-sm outline-none focus:ring-2 focus:ring-brand-400/30" />
                        </div>
                    </div>
                    <div className="max-h-64 overflow-y-auto p-1.5">
                        {filtered.length === 0 && <p className="px-3 py-5 text-center text-sm text-gray-400">No match</p>}
                        {filtered.map(o => {
                            const isSel = String(o.value) === String(value)
                            return (
                                <button key={o.value} type="button" onClick={() => pick(o.value)}
                                    className={`flex w-full items-center gap-2 rounded-xl px-3.5 py-3 text-left text-sm transition active:scale-[0.98]
                                        ${isSel ? `${palette.off} font-semibold` : 'text-gray-700 hover:bg-gray-50'}`}>
                                    <span className="flex-1 truncate">{o.label}</span>
                                    {typeof o.count === 'number' && <span className="text-xs text-gray-400">{o.count}</span>}
                                    {isSel && <HiCheck className={`shrink-0 ${palette.ic}`} />}
                                </button>
                            )
                        })}
                    </div>
                </div>}
        </div>
    )
}